import { type ActionEvent, Controller } from '@hotwired/stimulus'
import { cache } from '@hotwired/turbo'
import { within } from '@/lib/collection-query'
import { revealedScrollLeft, type Span } from '@/lib/filter-row'
import { formFields, type SegmentRoutes, segmentDestination, tidySearch } from '@/lib/search-form'

interface Placement {
  focus: string | null
  top: number
  row: number
}

// A live region that arrives filled with the new body announces nothing; it has to change after it exists.
const ANNOUNCE_DELAY_MS = 150

let pending: Placement | null = null
let restoring = false

document.addEventListener('turbo:visit', (event) => {
  restoring = (event as CustomEvent<{ action: string }>).detail.action === 'restore'
})

export default class extends Controller<HTMLElement> {
  static override targets = ['form', 'notice', 'control', 'count', 'status', 'row', 'filter']
  static override values = { list: String, segments: Array }

  declare readonly formTarget: HTMLFormElement
  declare readonly noticeTarget: HTMLElement
  declare readonly controlTargets: HTMLSelectElement[]
  declare readonly countTarget: HTMLElement
  declare readonly statusTarget: HTMLElement
  declare readonly rowTarget: HTMLElement
  declare readonly filterTargets: HTMLElement[]
  declare readonly listValue: string
  declare readonly segmentsValue: SegmentRoutes

  override connect() {
    cache.exemptPageFromPreview()
    this.noticeTarget.hidden = false
    for (const control of this.controlTargets) {
      control.setAttribute('aria-describedby', this.noticeTarget.id)
    }
  }

  apply({ currentTarget }: Event) {
    if (currentTarget instanceof HTMLElement) currentTarget.focus()
    this.formTarget.requestSubmit()
  }

  route() {
    this.formTarget.action =
      segmentDestination(formFields(this.formTarget), this.segmentsValue)?.path ?? this.listValue
  }

  remember({ params }: ActionEvent) {
    const focus = typeof params.focus === 'string' ? params.focus : document.activeElement?.id
    pending = {
      focus: focus || null,
      top: this.countTarget.getBoundingClientRect().top,
      row: this.rowTarget.scrollLeft,
    }
  }

  follow({ detail }: CustomEvent<{ url: string }>) {
    if (pending && !within(new URL(detail.url).pathname, this.listValue)) pending = null
  }

  prune({ formData }: FormDataEvent) {
    tidySearch(formData)
    const destination = segmentDestination(formData, this.segmentsValue)
    for (const field of destination?.carried ?? []) formData.delete(field)
  }

  reset() {
    this.formTarget.reset()
    this.statusTarget.textContent = ''
  }

  settle() {
    const placement = pending
    pending = null

    const control = placement?.focus ? document.getElementById(placement.focus) : null
    this.revealPills(placement?.row ?? 0, control)
    if (placement) this.place(placement, control)
    if (placement || restoring) this.announce()
  }

  private place({ top }: Placement, control: HTMLElement | null) {
    control?.focus({ preventScroll: true })
    window.scrollBy(0, this.countTarget.getBoundingClientRect().top - Math.max(top, 0))
    control?.scrollIntoView({ block: 'nearest' })
  }

  private announce() {
    window.setTimeout(() => {
      this.statusTarget.textContent = this.countTarget.textContent?.trim() ?? ''
    }, ANNOUNCE_DELAY_MS)
  }

  // Only scrollLeft, never scrollIntoView: the row must never move the document.
  private revealPills(from: number, control: HTMLElement | null) {
    const row = this.rowTarget
    if (row.scrollWidth <= row.clientWidth) return

    const origin = row.getBoundingClientRect().left - row.scrollLeft
    const span = (pill: HTMLElement): Span => {
      const { left, right } = pill.getBoundingClientRect()
      return { left: left - origin, right: right - origin }
    }
    const used = this.filterTargets.find((pill) => control && pill.contains(control))
    row.scrollLeft = revealedScrollLeft(
      {
        scrollLeft: from,
        width: row.clientWidth,
        inset: Number.parseFloat(getComputedStyle(row).paddingInlineStart),
      },
      this.filterTargets.filter((pill) => pill.dataset.applied === 'true').map(span),
      used ? span(used) : null,
    )
  }
}
